import { AnalyticsService } from '@alison/analytics/client';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  COMPETITION_FILTER_INDEX,
  COMPETITION_FILTER_KEYS,
  type CompetitionFilterKey,
} from '../components/filters/competitionFilterCatalog';
import { commaSeparatedArray } from './search-codec';

const SEARCH_PREFIX = 'c_';

export type AddedCompetitionFilter = { key: CompetitionFilterKey; values: string[] };

export type CompetitionSearch = Record<string, string[] | undefined>;

const searchKey = (key: CompetitionFilterKey): string => `${SEARCH_PREFIX}${key}`;

function dropOrphans(added: AddedCompetitionFilter[]): AddedCompetitionFilter[] {
  const present = new Set(added.map((filter) => filter.key));
  return added.filter((filter) => {
    const dependsOn = COMPETITION_FILTER_INDEX[filter.key].dependsOn;
    return dependsOn === undefined || present.has(dependsOn);
  });
}

function withoutFilterAndDependents(
  added: AddedCompetitionFilter[],
  key: CompetitionFilterKey,
): AddedCompetitionFilter[] {
  return dropOrphans(added.filter((filter) => filter.key !== key));
}

export function competitionFiltersFromSearch(
  search: Record<string, unknown>,
): AddedCompetitionFilter[] {
  const added: AddedCompetitionFilter[] = [];
  for (const key of COMPETITION_FILTER_KEYS) {
    const raw = search[searchKey(key)];
    if (raw === undefined) continue;
    added.push({ key, values: commaSeparatedArray.catch([]).parse(raw) });
  }
  return dropOrphans(added);
}

export function validateCompetitionFilterSearch(
  search: Record<string, unknown>,
): Record<string, string[]> {
  return Object.fromEntries(
    competitionFiltersFromSearch(search).map((filter) => [searchKey(filter.key), filter.values]),
  );
}

export function searchFromFilters(
  previous: CompetitionSearch,
  added: AddedCompetitionFilter[],
): CompetitionSearch {
  const base = { ...previous };
  for (const key of COMPETITION_FILTER_KEYS) delete base[searchKey(key)];
  for (const filter of added) base[searchKey(filter.key)] = filter.values;
  return base;
}

export function useCompetitionFilterSearch() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const added = useMemo(() => competitionFiltersFromSearch(search), [search]);

  const setFilters = (next: AddedCompetitionFilter[]): void => {
    void navigate({
      to: '.',
      replace: true,
      search: (previous) => searchFromFilters(previous as CompetitionSearch, next),
    });
  };

  return {
    added,
    addFilter: (key: CompetitionFilterKey): void => {
      AnalyticsService.track('Competition Filter - Added', { filter: key });
      setFilters([...added, { key, values: [] }]);
    },
    removeFilter: (key: CompetitionFilterKey): void => {
      AnalyticsService.track('Competition Filter - Removed', { filter: key });
      setFilters(withoutFilterAndDependents(added, key));
    },
    patchFilter: (key: CompetitionFilterKey, values: string[]): void =>
      setFilters(
        added.some((filter) => filter.key === key)
          ? added.map((filter) => (filter.key === key ? { ...filter, values } : filter))
          : [...added, { key, values }],
      ),
  };
}
